import { useEffect, useMemo, useRef, useState } from 'react';
import { isSphereId, sphereDefaultName, type Locale } from '@superposition/domain';
import * as api from '../../lib/api.js';
import { haptic } from '../../lib/telegram.js';
import { matrixColor } from '../../lib/palette.js';
import { useSphereName } from '../../lib/names.js';
import { useT } from '../../i18n/index.js';
import { useLocale } from '../../lib/profile.js';

/**
 * «метод» — книга по главам. текст приходит с сервера по сессии и рисуется
 * текстовыми узлами: никакой разметки из книги в DOM не исполняется.
 */

// главы не меняются между запусками, поэтому держим их в памяти вкладки;
// у каждого языка своя книга
const cache = new Map<string, api.BookChapter>();
const indexCache = new Map<Locale, api.BookIndex>();
const chapterKey = (locale: Locale, id: string): string => `${locale}:${id}`;

function Spans({ spans }: { spans: api.BookSpan[] }): React.ReactNode {
  return (
    <>
      {spans.map((s, i) => {
        if (s.strong) return <strong key={i}>{s.text}</strong>;
        if (s.em) return <em key={i}>{s.text}</em>;
        return <span key={i}>{s.text}</span>;
      })}
    </>
  );
}

const fmtTime = (sec: number): string => {
  const t = Math.max(0, Math.floor(sec));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

// играет одна практика за раз: новая ставит на паузу предыдущую
let playing: HTMLAudioElement | null = null;

/** аудиопрактика прямо в тексте главы: нажал — началась */
function AudioPractice({ block }: { block: Extract<api.BookBlock, { kind: 'audio' }> }): React.ReactNode {
  const ref = useRef<HTMLAudioElement>(null);
  const [on, setOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(block.duration);
  const [failed, setFailed] = useState(false);
  const sphereName = useSphereName();
  const t = useT();
  const locale = useLocale();
  const sphere = isSphereId(block.sphere) ? block.sphere : null;
  // цвет сферы как в матрице: там человек видит эти сферы каждый день
  const color = sphere ? matrixColor(sphere).c : 'var(--red)';

  // уход с главы останавливает запись, чтобы звук не играл «из ниоткуда»
  useEffect(() => {
    const el = ref.current;
    return () => {
      if (el && playing === el) playing = null;
      el?.pause();
    };
  }, []);

  const toggle = async (): Promise<void> => {
    const el = ref.current;
    if (!el) return;
    haptic('light');
    if (!el.paused) {
      el.pause();
      return;
    }
    if (playing && playing !== el) playing.pause();
    playing = el;
    setFailed(false);
    setLoading(true);
    try {
      await el.play();
      // экран блокировки и шторка показывают, что играет
      const ms = (navigator as { mediaSession?: MediaSession }).mediaSession;
      if (ms && typeof MediaMetadata !== 'undefined') {
        ms.metadata = new MediaMetadata({ title: t.method.mediaTitle({ title: block.title }), artist: t.common.brand });
      }
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // название по сфере: если человек переименовал сферу, видно его слово
  const base = t.lake.practiceTitle({ title: block.title });
  const [first, ...rest] = base.split(' · ');
  const title = sphere && first === sphereDefaultName(sphere, locale) ? [sphereName(sphere), ...rest].join(' · ') : base;

  return (
    <div className={`bk-audio${on ? ' on' : ''}`} style={{ ['--c' as string]: color }}>
      <button
        type="button"
        className="play"
        aria-label={on ? t.method.pauseAria({ title }) : t.method.listenAria({ title })}
        onClick={() => void toggle()}
      >
        {loading ? <span className="spin" aria-hidden="true" /> : on ? <span className="pause" aria-hidden="true" /> : <span className="tri" aria-hidden="true" />}
      </button>
      <div className="meta">
        <span className="t">{t.method.practice({ title })}</span>
        <input
          type="range"
          min={0}
          max={Math.max(1, Math.round(dur))}
          step={1}
          value={Math.round(pos)}
          aria-label={t.method.seekAria({ title })}
          onChange={(e) => {
            const el = ref.current;
            const v = Number(e.target.value);
            if (el) el.currentTime = v;
            setPos(v);
          }}
        />
        <span className="time">
          {failed ? t.method.playFailed : `${fmtTime(pos)} / ${fmtTime(dur)}`}
        </span>
      </div>
      <audio
        ref={ref}
        src={block.src}
        preload="none"
        onPlay={() => setOn(true)}
        onPause={() => setOn(false)}
        onEnded={() => {
          setOn(false);
          setPos(0);
        }}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.currentTarget.duration)) setDur(e.currentTarget.duration);
        }}
        onError={() => {
          setFailed(true);
          setLoading(false);
        }}
      />
    </div>
  );
}

function Block({ block }: { block: api.BookBlock }): React.ReactNode {
  if (block.kind === 'audio') {
    // только своя статика: CSP разрешает media-src 'self'
    if (!/^\/media\/book-audio\/[a-z0-9.-]+\.m4a$/.test(block.src)) return null;
    return <AudioPractice block={block} />;
  }
  if (block.kind === 'table') {
    return (
      <div className="bk-table">
        <table>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.kind === 'img') {
    // картинка берётся только со своего origin: CSP разрешает img-src 'self'
    if (!/^\/book\/img\/[a-z0-9.-]+\.svg$/.test(block.src)) return null;
    return (
      <figure className={block.role === 'cover' ? 'bk-fig bk-cover' : 'bk-fig'}>
        <img
          src={block.src}
          width={block.w}
          height={block.h}
          alt={block.alt}
          loading={block.role === 'cover' ? 'eager' : 'lazy'}
          decoding="async"
        />
        {block.caption ? <figcaption>{block.caption}</figcaption> : null}
      </figure>
    );
  }
  if (block.kind === 'h') return <h4><Spans spans={block.spans} /></h4>;
  if (block.kind === 'li') return <li><Spans spans={block.spans} /></li>;
  if (block.kind === 'quote') return <blockquote><Spans spans={block.spans} /></blockquote>;
  return <p><Spans spans={block.spans} /></p>;
}

export function MethodScreen({
  openId,
  openAnchor,
  onOpened,
}: {
  /** глава, на которую привёл наблюдатель */
  openId?: string | null;
  /** фраза в главе, к которой прокрутить (описание персоны) */
  openAnchor?: string | null;
  onOpened?: () => void;
}): React.ReactNode {
  const t = useT();
  const locale = useLocale();
  const [index, setIndex] = useState<api.BookIndex | null>(indexCache.get(locale) ?? null);
  const [chapterId, setChapterId] = useState<string | null>(openId ?? null);
  const [chapter, setChapter] = useState<api.BookChapter | null>(
    openId ? (cache.get(chapterKey(locale, openId)) ?? null) : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const topRef = useRef<HTMLDivElement | null>(null);
  const [anchor, setAnchor] = useState<string | null>(openAnchor ?? null);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const known = indexCache.get(locale);
    if (known) {
      setIndex(known);
      return;
    }
    let alive = true;
    api
      .fetchBook()
      .then((b) => {
        indexCache.set(locale, b);
        if (alive) setIndex(b);
      })
      .catch((err: unknown) => alive && setError(api.describeFailure(err, t)));
    return () => {
      alive = false;
    };
  }, [locale]);

  useEffect(() => {
    if (openId) {
      setChapterId(openId);
      setAnchor(openAnchor ?? null);
      onOpened?.();
    }
  }, [openId, openAnchor, onOpened]);

  // блок, с которого начинается нужное место главы
  const anchorAt = useMemo(() => {
    if (!anchor || !chapter) return -1;
    return chapter.blocks.findIndex(
      (b) => 'spans' in b && b.spans.map((sp) => sp.text).join('').includes(anchor),
    );
  }, [anchor, chapter]);

  useEffect(() => {
    if (anchorAt < 0) return;
    // после отрисовки главы: к описанию персоны, а не к началу
    const timer = setTimeout(() => anchorRef.current?.scrollIntoView({ block: 'start' }), 60);
    return () => clearTimeout(timer);
  }, [anchorAt]);

  useEffect(() => {
    if (!chapterId) return;
    const known = cache.get(chapterKey(locale, chapterId));
    if (known) {
      setChapter(known);
      return;
    }
    let alive = true;
    setLoading(true);
    api
      .fetchChapter(chapterId)
      .then((c) => {
        cache.set(chapterKey(locale, c.id), c);
        if (alive) setChapter(c);
      })
      .catch((err: unknown) => alive && setError(api.describeFailure(err, t)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [chapterId, locale]);

  const numbered = useMemo(() => {
    if (!index) return [];
    return index.chapters.map((c) => ({
      ...c,
      // «глава 7. …» → номер отдельно, чтобы заголовок в списке был коротким
      short: c.title.replace(/^(глава|chapter)\s+\d+\.?\s*/i, ''),
      num: /^(?:глава|chapter)\s+(\d+)/i.exec(c.title)?.[1] ?? null,
    }));
  }, [index]);

  if (error && !chapter && !index) {
    return (
      <section className="fade" aria-labelledby="mt-title">
        <h2 className="screen-t" id="mt-title">
          {t.method.title}
        </h2>
        <p className="obs-empty">{error}</p>
      </section>
    );
  }

  if (chapterId) {
    return (
      <section className="fade bk" aria-labelledby="mt-title" ref={topRef}>
        <button
          type="button"
          className="bk-back"
          onClick={() => {
            haptic('light');
            setChapterId(null);
            setChapter(null);
            setAnchor(null);
            window.scrollTo(0, 0);
          }}
        >
          {t.method.allChapters}
        </button>
        <h2 className="screen-t" id="mt-title">
          {chapter?.title ?? t.method.chapter}
        </h2>
        {loading && !chapter ? <p className="obs-empty">{t.method.openingChapter}</p> : null}
        {chapter ? (
          <article className="bk-text">
            {chapter.blocks.map((b, i) =>
              i === anchorAt ? (
                <div key={i} ref={anchorRef} className="bk-anchor">
                  <Block block={b} />
                </div>
              ) : (
                <Block key={i} block={b} />
              ),
            )}
          </article>
        ) : null}
        {error && !chapter ? <p className="obs-empty">{error}</p> : null}
      </section>
    );
  }

  return (
    <section className="fade" aria-labelledby="mt-title">
      <h2 className="screen-t" id="mt-title">
        {t.method.title}
      </h2>
      <p className="obs-empty" style={{ marginBottom: 14 }}>
        {t.method.intro}
      </p>
      <ul className="bk-list">
        {numbered.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => {
                haptic('select');
                setChapterId(c.id);
                window.scrollTo(0, 0);
              }}
            >
              <span className="n">{c.num ?? '·'}</span>
              <span className="t">{c.short || c.title}</span>
            </button>
          </li>
        ))}
      </ul>
      {!index ? <p className="obs-empty">{t.method.loadingIndex}</p> : null}
    </section>
  );
}
